import { useMemo } from 'react';

import { resolveBankDossier } from '../../../api/workOrders/bankDossier';
import { WorkOrderData } from '../../../api/workOrders/types';
import { Constants } from '../../../constants/bankDossier';
import { Money } from '../../UI/Money';
import { Card, Hint, Label, Strip, Value } from './styles';

interface DossierKpisProps {
  rows: WorkOrderData[];
  /** Total records when the list is paginated server-side; defaults to rows.length. */
  total?: number;
  loading?: boolean;
  /** Scope caption for the value KPIs, e.g. "nesta página". */
  hint?: string;
}

/**
 * KPI strip above work order lists: count, total appraised value and how
 * many OS already carry an appraisal value.
 */
export function DossierKpis({
  rows,
  total,
  loading = false,
  hint,
}: DossierKpisProps): JSX.Element {
  const { appraised, withValue } = useMemo(() => {
    let sum = 0;
    let count = 0;
    rows.forEach((row) => {
      const value = resolveBankDossier(row)?.appraisal_value;
      if (value != null) {
        sum += value;
        count += 1;
      }
    });
    return { appraised: sum, withValue: count };
  }, [rows]);

  const { list } = Constants;

  return (
    <Strip role="group" aria-label={list.kpiTotal}>
      <Card>
        <Label>{list.kpiTotal}</Label>
        <Value>{loading ? '…' : total ?? rows.length}</Value>
      </Card>
      <Card>
        <Label>
          {list.kpiAppraised}
          {hint && <Hint>{hint}</Hint>}
        </Label>
        <Value>{loading ? '…' : <Money value={appraised} />}</Value>
      </Card>
      <Card>
        <Label>
          {list.kpiWithValue}
          {hint && <Hint>{hint}</Hint>}
        </Label>
        <Value>{loading ? '…' : withValue}</Value>
      </Card>
    </Strip>
  );
}
